'use client'

import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/Button'
import { UserPlus, AlertCircle } from 'lucide-react'

interface GracefulErrorHandlerProps {
  error: string
  email: string
  onNavigateToSignup: (email: string) => void
  onDismiss: () => void
}

const GracefulErrorHandler: React.FC<GracefulErrorHandlerProps> = ({
  error,
  email,
  onNavigateToSignup,
  onDismiss
}) => {
  // Local UI state not needed after narrowing conditions; keep component lean

  // Show this banner ONLY when the backend indicates the user doesn't exist.
  // Do NOT match generic credential errors (which usually mean wrong password).
  const normalized = error.toLowerCase()
  const isUserNotFoundError =
    normalized.includes('user not found') ||
    normalized.includes('no user found') ||
    normalized.includes('does not exist') ||
    normalized.includes('not registered')

  if (!isUserNotFoundError) {
    return null
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.95 }}
        className="rounded-2xl p-5 mb-6"
        style={{ background: 'rgba(245, 158, 11, 0.10)', border: '1px solid rgba(245, 158, 11, 0.25)' }}
      >
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="text-sm font-bold text-amber-300 mb-1">
              Account not found
            </h4>
            <p className="text-sm text-amber-200/80 mb-3">
              We couldn&apos;t find an account with the email <strong className="text-white">{email}</strong>. 
              Would you like to create a new account instead?
            </p>
            
            <div className="flex space-x-3">
              <Button
                onClick={() => onNavigateToSignup(email)}
                className="flex items-center space-x-2 bg-amber-500 hover:bg-amber-600 text-slate-950 px-4 py-2 rounded-xl text-sm font-bold transition-colors"
              >
                <UserPlus className="w-4 h-4" />
                <span>Create Account</span>
              </Button>
              
              <Button
                onClick={onDismiss}
                variant="outline"
                className="px-4 py-2 rounded-xl text-sm font-medium border-white/10 text-white/70 hover:bg-white/5 transition-colors"
              >
                Try Again
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

export default GracefulErrorHandler


